/**
 * P_WEEKLY_REPORT_CARD_1.0 — Executive Ritual Engine.
 *
 * The Weekly Report Card is the Tuesday-morning ritual. It is a
 * READING surface, not a dashboard. Visual priorities, locked by spec:
 *
 *     Beauty       >  Density
 *     Typography   >  Artwork
 *     Reading      >  Scanning
 *     Magazine     >  Dashboard
 *
 * References: Apple Screen Time, Spotify Wrapped, Morning Brew,
 * Monocle Magazine, shareholder letters. NOT Power BI, NOT Salesforce.
 *
 * Sections (vertical, generously spaced):
 *   1. Cover                — logo, week, greeting, subtitle
 *   2. Executive Summary    — 4 large-number tiles
 *   3. Hall Of Fame         — 1 hero (Location Of The Week) + 3 supporting
 *   4. This Week In One Minute — up to 5 large timeline rows
 *   5. Closing
 *
 * Phase 2 deferred: HTML email rendering, FR/ES, Archive, Employee
 * of the Week, Looking Back, Team Celebrations, Customer Comment.
 */
import { useEffect, useMemo, useState } from 'react';
import { sortByName } from '../lib/sortByName';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import { useAuth } from '../lib/auth';
import api, { weeklyReportAPI, organizationsAPI } from '../lib/api';
import WeeklyReportArchive from '../components/weeklyReport/Archive';
import WeeklyReportDistribution from '../components/weeklyReport/DistributionCenter';
import WeeklyReportSettings from '../components/weeklyReport/Settings';
import WeeklyReportRecognition from '../components/weeklyReport/Recognition';

// ─────────────────────────────────────────────────────────────────────
// Restrained palette per P_WEEKLY_REPORT_CARD_2.2 — color guides the
// eye, never decorates the page. Mirrors the email exactly so the
// publication identity carries inbox-to-portal.
// ─────────────────────────────────────────────────────────────────────
const PALETTE = {
    value: '#6EE7B7',        // 💰 soft emerald
    reputation: '#E8C77B',   // ⭐ warm champagne
    engagement: '#F6A26B',   // 👥 soft orange
    recoveries: '#7CC3F0',   // 🔄 sky blue
    deltaUp: '#7AD3A4',      // muted positive green
    deltaDown: '#E8C77B',    // warm champagne for negative (no shouting)
    streak: '#F6A26B',       // soft orange streak chip
};

// ─────────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────────
export default function WeeklyReportCard() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const pinnedSnapshotId = searchParams.get('snapshot_id') || null;
    const urlTab = searchParams.get('tab') || 'current';
    const { user, isSuperAdmin } = useAuth();

    // Pinning a snapshot via deep link always forces the Current
    // Report tab — the inbox → portal contract is "show me this week".
    const activeTab = pinnedSnapshotId ? 'current' : urlTab;

    const [organizations, setOrganizations] = useState([]);
    const [selectedOrgId, setSelectedOrgId] = useState(user?.organization_id || null);
    const [mode, setMode] = useState('snapshot');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [actionBusy, setActionBusy] = useState(false);

    const setActiveTab = (next) => {
        const params = new URLSearchParams(searchParams);
        params.set('tab', next);
        params.delete('snapshot_id');
        setSearchParams(params, { replace: true });
    };

    // Super admin: list every org so they can pick one.
    useEffect(() => {
        if (!isSuperAdmin()) return;
        (async () => {
            try {
                const res = await organizationsAPI.list();
                const list = Array.isArray(res.data) ? res.data : (res.data?.items || []);
                setOrganizations(sortByName(list));
                if (!selectedOrgId && list.length > 0) {
                    setSelectedOrgId(list[0].id);
                }
            } catch (e) {
                // Non-fatal: super admin can still type/inspect.
            }
        })();
    }, []);

    // Load whenever (org, mode, pinned snapshot) changes.
    useEffect(() => {
        if (!selectedOrgId) {
            setLoading(false);
            return undefined;
        }
        let cancelled = false;
        setLoading(true);
        setError(null);
        // Deep-linked emails always read the immutable snapshot —
        // never live data, never "the latest". A pinned snapshot_id
        // forces mode=snapshot regardless of the toggle state.
        const effectiveMode = pinnedSnapshotId ? 'snapshot' : mode;
        (async () => {
            try {
                const res = await weeklyReportAPI.current(
                    selectedOrgId,
                    effectiveMode,
                    pinnedSnapshotId,
                );
                if (!cancelled) setData(res.data);
            } catch (e) {
                if (!cancelled) {
                    const detail = e?.response?.data?.detail || e?.message || 'error';
                    setError(detail);
                    setData(null);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [selectedOrgId, mode, pinnedSnapshotId]);

    const handleGenerate = async () => {
        if (!selectedOrgId) return;
        setActionBusy(true);
        try {
            const res = await weeklyReportAPI.generateNow(selectedOrgId);
            setData(res.data);
            setMode('snapshot');
            toast.success(t('weeklyReport.states.generated'));
        } catch (e) {
            toast.error(e?.response?.data?.detail || t('weeklyReport.states.error'));
        } finally {
            setActionBusy(false);
        }
    };

    const handleSend = async () => {
        if (!selectedOrgId) return;
        if (!window.confirm(t('weeklyReport.confirm.sendNow'))) return;
        setActionBusy(true);
        try {
            const res = await weeklyReportAPI.sendNow(selectedOrgId, data?.id || null);
            const status = res.data?.status;
            const msg = status === 'sent'
                ? t('weeklyReport.states.sentOk', { count: (res.data?.recipients || []).length })
                : t('weeklyReport.states.sentFail', { error: res.data?.error || '' });
            (status === 'sent' ? toast.success : toast.error)(msg);
        } catch (e) {
            toast.error(e?.response?.data?.detail || t('weeklyReport.states.error'));
        } finally {
            setActionBusy(false);
        }
    };

    // ── v2.1 — Super-admin controls ─────────────────────────────────
    const [previewHtml, setPreviewHtml] = useState(null);
    const [sendTestOpen, setSendTestOpen] = useState(false);
    const [historyOpen, setHistoryOpen] = useState(false);
    const [historyItems, setHistoryItems] = useState([]);

    const handlePreviewEmail = async () => {
        if (!selectedOrgId) return;
        setActionBusy(true);
        try {
            const url = weeklyReportAPI.previewEmailUrl(selectedOrgId, { mode });
            const res = await api.get(url, { responseType: 'text' });
            setPreviewHtml(res.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || t('weeklyReport.states.error'));
        } finally {
            setActionBusy(false);
        }
    };

    const handleSendTest = async (recipient) => {
        if (!selectedOrgId || !recipient) return;
        setActionBusy(true);
        try {
            const res = await weeklyReportAPI.sendTest(selectedOrgId, recipient, data?.id || null);
            const ok = res.data?.status === 'sent';
            (ok ? toast.success : toast.error)(
                ok
                    ? t('weeklyReport.states.testSent', { recipient })
                    : t('weeklyReport.states.sentFail', { error: res.data?.error || '' })
            );
            setSendTestOpen(false);
        } catch (e) {
            toast.error(e?.response?.data?.detail || t('weeklyReport.states.error'));
        } finally {
            setActionBusy(false);
        }
    };

    const openResendPicker = async () => {
        if (!selectedOrgId) return;
        setActionBusy(true);
        try {
            const res = await weeklyReportAPI.history(selectedOrgId, 12);
            setHistoryItems(res.data?.items || []);
            setHistoryOpen(true);
        } catch (e) {
            toast.error(e?.response?.data?.detail || t('weeklyReport.states.error'));
        } finally {
            setActionBusy(false);
        }
    };

    const handleResend = async (snapshotId) => {
        setActionBusy(true);
        try {
            const res = await weeklyReportAPI.resend(selectedOrgId, snapshotId);
            const ok = res.data?.status === 'sent';
            (ok ? toast.success : toast.error)(
                ok
                    ? t('weeklyReport.states.resent', { count: (res.data?.recipients || []).length })
                    : t('weeklyReport.states.sentFail', { error: res.data?.error || '' })
            );
            setHistoryOpen(false);
        } catch (e) {
            toast.error(e?.response?.data?.detail || t('weeklyReport.states.error'));
        } finally {
            setActionBusy(false);
        }
    };

    const showEmptyState = !loading && (error === 'no_snapshot_for_organization' || (!data && !error));

    return (
        <div
            className="min-h-screen relative overflow-x-hidden"
            style={{
                background: 'linear-gradient(180deg, #050811 0%, #0a1224 40%, #050811 100%)',
                color: '#F5F1E8',
            }}
            data-testid="weekly-report-page"
        >
            <AmbientGlow />

            <div className="relative max-w-3xl mx-auto px-6 sm:px-10 pt-10 sm:pt-16 pb-32">
                <TopBar
                    onBack={() => navigate('/operational-base')}
                    backLabel={t('weeklyReport.actions.back')}
                />

                <TabBar
                    active={activeTab}
                    onChange={setActiveTab}
                    isSuperAdmin={isSuperAdmin()}
                    selectedOrgId={selectedOrgId}
                    onSelectOrg={setSelectedOrgId}
                    organizations={organizations}
                    t={t}
                />

                {activeTab === 'current' && (
                <>
                {/* Super-admin context strip — quiet, no shouting */}
                {isSuperAdmin() && (
                    <ContextStrip
                        organizations={organizations}
                        selectedOrgId={selectedOrgId}
                        onSelectOrg={setSelectedOrgId}
                        mode={mode}
                        onModeChange={setMode}
                        onGenerate={handleGenerate}
                        onSend={handleSend}
                        onPreviewEmail={handlePreviewEmail}
                        onSendTest={() => setSendTestOpen(true)}
                        onResend={openResendPicker}
                        actionBusy={actionBusy}
                        t={t}
                    />
                )}

                {/* Non-super-admin still gets the actions — quietly. */}
                {!isSuperAdmin() && selectedOrgId && (
                    <SimpleActionStrip
                        onGenerate={handleGenerate}
                        onSend={handleSend}
                        actionBusy={actionBusy}
                        t={t}
                    />
                )}

                {loading && (
                    <div className="py-32 flex flex-col items-center gap-6" data-testid="weekly-report-loading">
                        <Loader2 className="w-8 h-8 animate-spin opacity-50" />
                        <p className="text-sm tracking-wide opacity-60">
                            {t('weeklyReport.states.loading')}
                        </p>
                    </div>
                )}

                {!loading && showEmptyState && (
                    <EmptyState
                        onGenerate={handleGenerate}
                        actionBusy={actionBusy}
                        t={t}
                    />
                )}

                {!loading && error && error !== 'no_snapshot_for_organization' && (
                    <div
                        className="py-24 text-center text-sm opacity-60"
                        data-testid="weekly-report-error"
                    >
                        {t('weeklyReport.states.error')}
                    </div>
                )}

                {!loading && data && (
                    <article className="space-y-32 sm:space-y-40">
                        <CoverSection data={data} userName={user?.name} t={t} />
                        <ExecutiveSummarySection data={data} t={t} />
                        <HallOfFameSection data={data} t={t} />
                        <FirstStarSection data={data} t={t} />
                        <CustomerCommentSection data={data} t={t} />
                        <TeamCelebrationsSection data={data} t={t} />
                        <OneMinuteFeedSection data={data} t={t} />
                        <LookingBackSection data={data} t={t} />
                        <MomentumSection data={data} t={t} />
                        <ClosingSection t={t} />
                    </article>
                )}
                </>
                )}

                {activeTab === 'archive' && (
                    <WeeklyReportArchive organizationId={selectedOrgId} t={t} />
                )}

                {activeTab === 'recognition' && (
                    <WeeklyReportRecognition organizationId={selectedOrgId} t={t} />
                )}

                {activeTab === 'distribution' && (
                    <WeeklyReportDistribution
                        organizationId={selectedOrgId}
                        organizationName={
                            organizations.find((o) => o.id === selectedOrgId)?.name
                            || user?.organization_name
                            || ''
                        }
                        isSuperAdmin={isSuperAdmin()}
                        organizations={organizations}
                        t={t}
                    />
                )}

                {activeTab === 'settings' && (
                    <WeeklyReportSettings organizationId={selectedOrgId} t={t} />
                )}
            </div>

            {previewHtml && (
                <EmailPreviewModal
                    html={previewHtml}
                    onClose={() => setPreviewHtml(null)}
                    t={t}
                />
            )}

            {sendTestOpen && (
                <SendTestModal
                    onClose={() => setSendTestOpen(false)}
                    onSubmit={handleSendTest}
                    busy={actionBusy}
                    defaultEmail={user?.email || ''}
                    t={t}
                />
            )}

            {historyOpen && (
                <ResendHistoryModal
                    items={historyItems}
                    onClose={() => setHistoryOpen(false)}
                    onPick={handleResend}
                    busy={actionBusy}
                    t={t}
                />
            )}
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────
// v2.1 — Super-admin modals (Preview / Send Test / Re-send)
// ─────────────────────────────────────────────────────────────────────
const ModalShell = ({ onClose, children, testId, wide = false }) => (
    <div
        className="fixed inset-0 z-50 flex items-center justify-center px-4"
        style={{ background: 'rgba(2, 6, 23, 0.85)' }}
        data-testid={testId}
        onClick={onClose}
    >
        <div
            className={`relative w-full ${wide ? 'max-w-3xl' : 'max-w-md'} bg-[#0a1224] border border-white/10 rounded-2xl p-8 shadow-2xl`}
            onClick={(e) => e.stopPropagation()}
        >
            {children}
        </div>
    </div>
);

const EmailPreviewModal = ({ html, onClose, t }) => (
    <ModalShell onClose={onClose} testId="weekly-report-preview-modal" wide>
        <div className="flex items-center justify-between mb-6">
            <h3 className="text-sm uppercase tracking-[0.2em] opacity-70">
                {t('weeklyReport.actions.previewEmail')}
            </h3>
            <button
                type="button"
                onClick={onClose}
                data-testid="weekly-report-preview-close-btn"
                className="text-xs uppercase tracking-[0.2em] opacity-60 hover:opacity-100"
            >
                {t('weeklyReport.actions.close')}
            </button>
        </div>
        <iframe
            title="weekly-report-preview"
            srcDoc={html}
            data-testid="weekly-report-preview-iframe"
            className="w-full bg-black rounded-xl border border-white/10"
            style={{ height: '70vh' }}
        />
    </ModalShell>
);

const SendTestModal = ({ onClose, onSubmit, busy, defaultEmail, t }) => {
    const [email, setEmail] = useState(defaultEmail);
    return (
        <ModalShell onClose={onClose} testId="weekly-report-send-test-modal">
            <h3 className="text-sm uppercase tracking-[0.2em] opacity-70 mb-6">
                {t('weeklyReport.actions.sendTest')}
            </h3>
            <p className="text-sm opacity-60 mb-6 leading-relaxed">
                {t('weeklyReport.sendTest.helper')}
            </p>
            <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('weeklyReport.sendTest.placeholder')}
                data-testid="weekly-report-send-test-email-input"
                className="w-full bg-transparent border border-white/20 rounded-lg px-4 py-3 text-base focus:outline-none focus:border-white/60"
            />
            <div className="mt-8 flex items-center justify-end gap-3">
                <button
                    type="button"
                    onClick={onClose}
                    data-testid="weekly-report-send-test-cancel-btn"
                    className="px-3 py-1.5 text-xs uppercase tracking-[0.18em] opacity-60 hover:opacity-100"
                >
                    {t('weeklyReport.actions.cancel')}
                </button>
                <button
                    type="button"
                    onClick={() => onSubmit(email.trim())}
                    disabled={busy || !email.trim()}
                    data-testid="weekly-report-send-test-confirm-btn"
                    className="px-4 py-2 text-xs uppercase tracking-[0.18em] border border-white/30 rounded-full hover:border-white/80 disabled:opacity-40"
                >
                    {t('weeklyReport.actions.send')}
                </button>
            </div>
        </ModalShell>
    );
};

const ResendHistoryModal = ({ items, onClose, onPick, busy, t }) => (
    <ModalShell onClose={onClose} testId="weekly-report-resend-modal">
        <div className="flex items-center justify-between mb-6">
            <h3 className="text-sm uppercase tracking-[0.2em] opacity-70">
                {t('weeklyReport.actions.resend')}
            </h3>
            <button
                type="button"
                onClick={onClose}
                className="text-xs uppercase tracking-[0.2em] opacity-60 hover:opacity-100"
            >
                {t('weeklyReport.actions.close')}
            </button>
        </div>
        <p className="text-sm opacity-60 mb-6 leading-relaxed">
            {t('weeklyReport.resend.helper')}
        </p>
        {items.length === 0 ? (
            <p className="text-sm opacity-50">{t('weeklyReport.resend.empty')}</p>
        ) : (
            <div className="space-y-2 max-h-[60vh] overflow-y-auto">
                {items.map((it) => (
                    <button
                        key={it.id}
                        type="button"
                        onClick={() => onPick(it.id)}
                        disabled={busy}
                        data-testid={`weekly-report-resend-pick-${it.id}`}
                        className="w-full text-left px-4 py-3 rounded-lg border border-white/10 hover:border-white/40 transition-colors disabled:opacity-40"
                    >
                        <div className="text-base">
                            {t('weeklyReport.weekEnding', { date: it.week_end })}
                        </div>
                        <div className="text-xs opacity-50 mt-1">
                            {new Date(it.created_at).toLocaleString()}
                        </div>
                    </button>
                ))}
            </div>
        )}
    </ModalShell>
);

// ─────────────────────────────────────────────────────────────────────
// Ambient backdrop — very subtle, no neon.
// ─────────────────────────────────────────────────────────────────────
const AmbientGlow = () => (
    <>
        <div
            className="absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full blur-3xl pointer-events-none"
            style={{ background: 'radial-gradient(closest-side, rgba(96, 165, 250, 0.10) 0%, transparent 70%)' }}
            aria-hidden="true"
        />
        <div
            className="absolute bottom-0 right-0 w-[600px] h-[600px] rounded-full blur-3xl pointer-events-none"
            style={{ background: 'radial-gradient(closest-side, rgba(251, 191, 36, 0.06) 0%, transparent 70%)' }}
            aria-hidden="true"
        />
    </>
);

// ─────────────────────────────────────────────────────────────────────
// Chrome
// ─────────────────────────────────────────────────────────────────────
const TopBar = ({ onBack, backLabel }) => (
    <div className="mb-12 sm:mb-16">
        <button
            type="button"
            onClick={onBack}
            data-testid="weekly-report-back-btn"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] opacity-50 hover:opacity-100 transition-opacity"
        >
            <ArrowLeft className="w-3 h-3" />
            {backLabel}
        </button>
    </div>
);

// Tab navigation for Weekly Reports (v2.3 Distribution Center).
// Magazine-style: four quiet pill links, generous spacing, no big chrome.
const TabBar = ({ active, onChange, isSuperAdmin, selectedOrgId, onSelectOrg, organizations, t }) => {
    const tabs = [
        { id: 'current', labelKey: 'weeklyReport.tabs.current' },
        { id: 'archive', labelKey: 'weeklyReport.tabs.archive' },
        { id: 'recognition', labelKey: 'weeklyReport.tabs.recognition' },
        { id: 'distribution', labelKey: 'weeklyReport.tabs.distribution' },
        { id: 'settings', labelKey: 'weeklyReport.tabs.settings' },
    ];
    return (
        <div className="mb-12 sm:mb-16 flex flex-col gap-6" data-testid="weekly-report-tabbar">
            <nav className="flex flex-wrap gap-x-8 gap-y-3 border-b border-white/10 pb-4">
                {tabs.map((tab) => {
                    const isActive = active === tab.id;
                    return (
                        <button
                            key={tab.id}
                            type="button"
                            onClick={() => onChange(tab.id)}
                            data-testid={`weekly-report-tab-${tab.id}`}
                            className={`text-xs uppercase tracking-[0.22em] transition-colors ${
                                isActive ? 'text-white' : 'text-white/45 hover:text-white/80'
                            }`}
                        >
                            {t(tab.labelKey)}
                            {isActive && (
                                <span className="block mt-2 h-px bg-white" aria-hidden="true" />
                            )}
                        </button>
                    );
                })}
            </nav>

            {/* Org selector is visible on every tab for super_admin so
                the user can move between organizations without losing
                their place. Client admins are scoped to their own org. */}
            {isSuperAdmin && active !== 'current' && (
                <div className="flex items-center gap-3 text-xs uppercase tracking-[0.18em] opacity-70">
                    <span className="opacity-50">{t('weeklyReport.actions.openOrgPicker')}</span>
                    <select
                        value={selectedOrgId || ''}
                        onChange={(e) => onSelectOrg(e.target.value)}
                        data-testid="weekly-report-tab-org-select"
                        className="bg-transparent border-b border-white/20 px-1 py-1 focus:outline-none focus:border-white/60"
                    >
                        {organizations.map((o) => (
                            <option key={o.id} value={o.id} className="bg-slate-900">
                                {o.name}
                            </option>
                        ))}
                    </select>
                </div>
            )}
        </div>
    );
};

const ContextStrip = ({
    organizations, selectedOrgId, onSelectOrg,
    mode, onModeChange, onGenerate, onSend,
    onPreviewEmail, onSendTest, onResend, actionBusy, t,
}) => (
    <div
        className="mb-16 sm:mb-24 flex flex-col gap-6 text-xs uppercase tracking-[0.18em] opacity-70"
        data-testid="weekly-report-context-strip"
    >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
                <span className="opacity-50">{t('weeklyReport.actions.openOrgPicker')}</span>
                <select
                    value={selectedOrgId || ''}
                    onChange={(e) => onSelectOrg(e.target.value)}
                    data-testid="weekly-report-org-select"
                    className="bg-transparent border-b border-white/20 px-1 py-1 text-xs uppercase tracking-[0.18em] focus:outline-none focus:border-white/60"
                >
                    {organizations.map((o) => (
                        <option key={o.id} value={o.id} className="bg-slate-900">
                            {o.name}
                        </option>
                    ))}
                </select>
            </div>
            <ModeToggle mode={mode} onChange={onModeChange} t={t} />
        </div>
        <div className="flex flex-wrap items-center gap-3">
            <ActionPill testId="weekly-report-generate-btn" onClick={onGenerate} busy={actionBusy} disabled={!selectedOrgId}>
                {t('weeklyReport.actions.generate')}
            </ActionPill>
            <ActionPill testId="weekly-report-preview-email-btn" onClick={onPreviewEmail} busy={actionBusy} disabled={!selectedOrgId}>
                {t('weeklyReport.actions.previewEmail')}
            </ActionPill>
            <ActionPill testId="weekly-report-send-test-btn" onClick={onSendTest} busy={actionBusy} disabled={!selectedOrgId}>
                {t('weeklyReport.actions.sendTest')}
            </ActionPill>
            <ActionPill testId="weekly-report-send-btn" onClick={onSend} busy={actionBusy} disabled={!selectedOrgId}>
                {t('weeklyReport.actions.sendNow')}
            </ActionPill>
            <ActionPill testId="weekly-report-resend-btn" onClick={onResend} busy={actionBusy} disabled={!selectedOrgId}>
                {t('weeklyReport.actions.resend')}
            </ActionPill>
        </div>
    </div>
);

const ActionPill = ({ children, onClick, busy, disabled, testId }) => (
    <button
        type="button"
        onClick={onClick}
        disabled={busy || disabled}
        data-testid={testId}
        className="px-3 py-1.5 text-xs uppercase tracking-[0.18em] border border-white/30 rounded-full hover:border-white/80 transition-colors disabled:opacity-40"
    >
        {children}
    </button>
);

const ModeToggle = ({ mode, onChange, t }) => (
    <div className="inline-flex border border-white/20 rounded-full overflow-hidden" data-testid="weekly-report-mode-toggle">
        {['snapshot', 'live'].map((m) => (
            <button
                key={m}
                type="button"
                onClick={() => onChange(m)}
                data-testid={`weekly-report-mode-${m}`}
                className={`px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] transition-colors ${
                    mode === m ? 'bg-white/15 text-white' : 'text-white/50 hover:text-white/80'
                }`}
            >
                {t(`weeklyReport.modes.${m}`)}
            </button>
        ))}
    </div>
);

const SimpleActionStrip = ({ onGenerate, onSend, actionBusy, t }) => (
    <div
        className="mb-16 sm:mb-24 flex items-center justify-end gap-3 text-xs uppercase tracking-[0.18em] opacity-70"
        data-testid="weekly-report-simple-action-strip"
    >
        <button
            type="button"
            onClick={onGenerate}
            disabled={actionBusy}
            data-testid="weekly-report-generate-btn"
            className="px-3 py-1.5 text-xs uppercase tracking-[0.18em] border border-white/30 rounded-full hover:border-white/80 transition-colors disabled:opacity-40"
        >
            {t('weeklyReport.actions.generate')}
        </button>
        <button
            type="button"
            onClick={onSend}
            disabled={actionBusy}
            data-testid="weekly-report-send-btn"
            className="px-3 py-1.5 text-xs uppercase tracking-[0.18em] border border-white/30 rounded-full hover:border-white/80 transition-colors disabled:opacity-40"
        >
            {t('weeklyReport.actions.send')}
        </button>
    </div>
);

const EmptyState = ({ onGenerate, actionBusy, t }) => (
    <div className="py-32 text-center" data-testid="weekly-report-empty-state">
        <p className="text-base sm:text-lg opacity-70 max-w-md mx-auto leading-relaxed">
            {t('weeklyReport.states.noSnapshot')}
        </p>
        <button
            type="button"
            onClick={onGenerate}
            disabled={actionBusy}
            data-testid="weekly-report-empty-generate-btn"
            className="mt-12 px-5 py-2.5 text-xs uppercase tracking-[0.2em] border border-white/30 rounded-full hover:border-white/80 transition-colors disabled:opacity-40"
        >
            {t('weeklyReport.actions.generate')}
        </button>
    </div>
);

// ─────────────────────────────────────────────────────────────────────
// Section 1 — Cover
// ─────────────────────────────────────────────────────────────────────
const CoverSection = ({ data, userName, t }) => {
    const snap = data.snapshot_json || {};
    const org = snap.organization || {};
    const week = snap.week || {};
    const firstName = (userName || '').split(' ')[0] || '';

    return (
        <section
            className="text-center pt-12 sm:pt-20"
            data-testid="weekly-report-cover"
        >
            {org.logo_url ? (
                <img
                    src={org.logo_url}
                    alt={org.name || ''}
                    className="mx-auto mb-12 sm:mb-16 max-h-20 sm:max-h-24 opacity-90"
                    data-testid="weekly-report-cover-logo"
                />
            ) : (
                <div
                    className="mx-auto mb-12 sm:mb-16 text-2xl sm:text-3xl font-light tracking-[0.18em] uppercase opacity-70"
                    data-testid="weekly-report-cover-org-name"
                >
                    {org.name || '—'}
                </div>
            )}

            <p
                className="text-xs sm:text-sm uppercase tracking-[0.32em] opacity-60 mb-8"
                data-testid="weekly-report-cover-eyebrow"
            >
                {t('weeklyReport.eyebrow')}
            </p>

            <h1
                className="text-4xl sm:text-6xl lg:text-7xl font-extralight tracking-tight leading-[1.1] mb-16 sm:mb-24"
                data-testid="weekly-report-cover-week"
            >
                {t('weeklyReport.weekEnding', { date: week.label || '' })}
            </h1>

            <p
                className="text-2xl sm:text-3xl font-light leading-relaxed mb-4"
                data-testid="weekly-report-cover-greeting"
            >
                {t('weeklyReport.greeting', { name: firstName || org.name || '' })}
            </p>

            <p
                className="text-base sm:text-lg opacity-70 leading-relaxed max-w-xl mx-auto"
                data-testid="weekly-report-cover-subtitle"
            >
                {t('weeklyReport.coverSubtitle')}
            </p>

            <p
                className="mt-20 sm:mt-32 text-[10px] uppercase tracking-[0.4em] opacity-30"
                data-testid="weekly-report-cover-poweredby"
            >
                {t('weeklyReport.poweredBy')}
            </p>
        </section>
    );
};

// ─────────────────────────────────────────────────────────────────────
// Section 2 — Executive Summary (4 magazine cards, restrained accents)
// ─────────────────────────────────────────────────────────────────────
const ExecutiveSummarySection = ({ data, t }) => {
    const metrics = data.snapshot_json?.metrics || {};
    const formatted = useMemo(() => formatMetrics(metrics), [metrics]);

    return (
        <section data-testid="weekly-report-exec-summary">
            <SectionHeading text={t('weeklyReport.execSummary.title')} testId="weekly-report-exec-summary-title" />

            <div className="mt-16 sm:mt-20 grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8">
                <MetricCard
                    glyph="💰"
                    accent={PALETTE.value}
                    label={t('weeklyReport.execSummary.valueGenerated')}
                    value={formatted.value}
                    deltaPct={metrics.value_delta_pct}
                    deltaLabel={t('weeklyReport.execSummary.vsLastWeek')}
                    testId="weekly-report-stat-value"
                />
                <MetricCard
                    glyph="⭐"
                    accent={PALETTE.reputation}
                    label={t('weeklyReport.execSummary.reputation')}
                    value={formatted.reputation.value}
                    sub={formatted.reputation.sub}
                    testId="weekly-report-stat-rating"
                />
                <MetricCard
                    glyph="👥"
                    accent={PALETTE.engagement}
                    label={t('weeklyReport.execSummary.engagement')}
                    value={formatted.engagements}
                    deltaPct={metrics.engagements_delta_pct}
                    deltaLabel={t('weeklyReport.execSummary.vsLastWeek')}
                    testId="weekly-report-stat-engagements"
                />
                <MetricCard
                    glyph="🔄"
                    accent={PALETTE.recoveries}
                    label={t('weeklyReport.execSummary.recoveries')}
                    value={formatted.recoveries}
                    deltaPct={metrics.recoveries_delta_pct}
                    deltaLabel={t('weeklyReport.execSummary.vsLastWeek')}
                    testId="weekly-report-stat-recoveries"
                />
            </div>
        </section>
    );
};

const MetricCard = ({ glyph, accent, label, value, sub, deltaPct, deltaLabel, testId }) => {
    const deltaStr = formatDelta(deltaPct);
    return (
        <div
            className="rounded-2xl p-8 sm:p-10 border border-white/10 bg-white/[0.025] backdrop-blur-sm"
            style={{ borderTop: `2px solid ${accent}` }}
            data-testid={testId}
        >
            <div className="flex items-center gap-3 mb-6">
                <span className="text-2xl leading-none" aria-hidden="true">{glyph}</span>
                <p
                    className="text-xs uppercase tracking-[0.24em]"
                    style={{ color: accent }}
                >
                    {label}
                </p>
            </div>
            <p
                className="text-5xl sm:text-6xl font-extralight tracking-tight leading-none"
                data-testid={`${testId}-value`}
            >
                {value}
            </p>
            {sub && (
                <p className="mt-5 text-sm opacity-60" data-testid={`${testId}-sub`}>
                    {sub}
                </p>
            )}
            {deltaStr && (
                <p
                    className="mt-5 text-sm tracking-wide"
                    style={{ color: deltaStr.color }}
                    data-testid={`${testId}-delta`}
                >
                    {deltaStr.symbol} {deltaStr.value}
                    <span className="opacity-50"> · {deltaLabel}</span>
                </p>
            )}
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────
// Section 3 — Hall Of Fame (narrative + dividers, no cards)
// ─────────────────────────────────────────────────────────────────────
const HallOfFameSection = ({ data, t }) => {
    const hof = data.snapshot_json?.hall_of_fame || {};
    const hero = hof.location_of_week;
    const streak = hero?.streak_weeks || 1;
    const streakKey = streak === 1 ? 'streakSingle' : (streak === 2 ? 'streakTwo' : 'streakMany');

    return (
        <section data-testid="weekly-report-hall-of-fame">
            <SectionHeading text={t('weeklyReport.hallOfFame.title')} testId="weekly-report-hof-title" />

            {/* Hero — Location Of The Week */}
            <div className="mt-16 sm:mt-20" data-testid="weekly-report-hof-hero">
                <p className="text-xs uppercase tracking-[0.32em] opacity-50 mb-6">
                    {t('weeklyReport.hallOfFame.locationOfWeek')}
                </p>
                {hero ? (
                    <>
                        <h3
                            className="text-5xl sm:text-7xl font-light tracking-tight leading-tight"
                            data-testid="weekly-report-hof-hero-name"
                        >
                            {hero.name}
                        </h3>
                        <p
                            className="mt-8 text-lg sm:text-xl opacity-70 leading-relaxed max-w-2xl font-light"
                            data-testid="weekly-report-hof-hero-narrative"
                        >
                            {t(`weeklyReport.hallOfFame.${streakKey}`, {
                                name: hero.name,
                                weeks: streak,
                                rating: hero.rating ?? '—',
                            })}
                        </p>

                        {/* Chips — rating + streak. Typography first; no boxy bling. */}
                        <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm sm:text-base opacity-90">
                            {hero.rating !== null && hero.rating !== undefined && (
                                <span
                                    data-testid="weekly-report-hof-hero-rating-chip"
                                    style={{ color: PALETTE.reputation }}
                                >
                                    <span aria-hidden="true">⭐</span> {hero.rating} {t('weeklyReport.hallOfFame.ratingWord')}
                                </span>
                            )}
                            {streak >= 2 && (
                                <span
                                    data-testid="weekly-report-hof-hero-streak-chip"
                                    style={{ color: PALETTE.streak }}
                                >
                                    <span aria-hidden="true">🔥</span> {t('weeklyReport.hallOfFame.streakChip', { weeks: streak })}
                                </span>
                            )}
                        </div>
                    </>
                ) : (
                    <p className="text-base sm:text-lg opacity-50">
                        {t('weeklyReport.hallOfFame.empty')}
                    </p>
                )}
            </div>

            {/* Subtle divider, then 3 award rows with generous spacing. */}
            <div
                className="mt-24 sm:mt-32 border-t border-white/10 pt-16 sm:pt-20 space-y-16 sm:space-y-20"
                data-testid="weekly-report-hof-awards"
            >
                <AwardRow
                    label={t('weeklyReport.hallOfFame.reputationLeader')}
                    name={hof.reputation_leader?.name}
                    subtitle={
                        hof.reputation_leader
                            ? t('weeklyReport.hallOfFame.reputationLeaderSubtitle', {
                                rating: hof.reputation_leader.avg_rating,
                                count: hof.reputation_leader.sample,
                            })
                            : null
                    }
                    testId="weekly-report-hof-reputation"
                />
                <AwardRow
                    label={t('weeklyReport.hallOfFame.phoenixAward')}
                    name={hof.phoenix_award?.name}
                    subtitle={
                        hof.phoenix_award
                            ? t('weeklyReport.hallOfFame.phoenixAwardSubtitle', {
                                pct: hof.phoenix_award.improvement_pct,
                            })
                            : null
                    }
                    testId="weekly-report-hof-phoenix"
                />
                <AwardRow
                    label={t('weeklyReport.hallOfFame.engagementChampion')}
                    name={hof.engagement_champion?.name}
                    subtitle={
                        hof.engagement_champion
                            ? t('weeklyReport.hallOfFame.engagementChampionSubtitle', {
                                taps: hof.engagement_champion.taps?.toLocaleString?.() || hof.engagement_champion.taps,
                            })
                            : null
                    }
                    testId="weekly-report-hof-engagement"
                />
            </div>
        </section>
    );
};

const AwardRow = ({ label, name, subtitle, testId }) => (
    <div data-testid={testId}>
        <p className="text-[11px] uppercase tracking-[0.28em] opacity-50 mb-4">{label}</p>
        {name ? (
            <>
                <h4 className="text-3xl sm:text-4xl font-light tracking-tight leading-snug">{name}</h4>
                {subtitle && (
                    <p className="mt-3 text-base sm:text-lg opacity-60">{subtitle}</p>
                )}
            </>
        ) : (
            <p className="text-base sm:text-lg opacity-40">—</p>
        )}
    </div>
);

// ─────────────────────────────────────────────────────────────────────
// Section 4 — This Week In One Minute (large ✓ bullets, max 5)
// ─────────────────────────────────────────────────────────────────────
const OneMinuteFeedSection = ({ data, t }) => {
    const feed = data.snapshot_json?.one_minute_feed || [];

    return (
        <section data-testid="weekly-report-feed">
            <SectionHeading text={t('weeklyReport.feed.title')} testId="weekly-report-feed-title" />

            <div className="mt-20 sm:mt-24 space-y-12 sm:space-y-16">
                {feed.length === 0 ? (
                    <p className="text-base sm:text-lg opacity-50">{t('weeklyReport.feed.empty')}</p>
                ) : (
                    feed.map((row, idx) => (
                        <FeedRow
                            key={`${row.kind}-${idx}`}
                            text={row.text}
                            testId={`weekly-report-feed-row-${idx}`}
                        />
                    ))
                )}
            </div>
        </section>
    );
};

const FeedRow = ({ text, testId }) => (
    <div className="flex items-start gap-6 sm:gap-8" data-testid={testId}>
        <span
            className="text-2xl sm:text-3xl font-light leading-none opacity-70 shrink-0 select-none mt-1"
            aria-hidden="true"
        >
            ✓
        </span>
        <p className="text-2xl sm:text-3xl font-light leading-relaxed">
            {text}
        </p>
    </div>
);

// ─────────────────────────────────────────────────────────────────────
// Section 5 — Closing
// ─────────────────────────────────────────────────────────────────────
const ClosingSection = ({ t }) => (
    <section className="text-center pt-16 pb-8" data-testid="weekly-report-closing">
        <h2
            className="text-3xl sm:text-5xl font-extralight tracking-tight mb-6"
            data-testid="weekly-report-closing-title"
        >
            {t('weeklyReport.closing.title')}
        </h2>
        <p className="text-lg sm:text-xl opacity-70 leading-relaxed max-w-md mx-auto">
            {t('weeklyReport.closing.body')}
        </p>
        <p className="mt-12 text-xs uppercase tracking-[0.32em] opacity-40">
            {t('weeklyReport.closing.signoff')}
        </p>
    </section>
);

// ─────────────────────────────────────────────────────────────────────
// v3.0 Recognition layer — gracefully omitted when absent
// ─────────────────────────────────────────────────────────────────────
const FirstStarSection = ({ data, t }) => {
    const star = data.snapshot_json?.first_star;
    if (!star || !(star.name || '').trim()) return null;
    return (
        <section data-testid="weekly-report-first-star">
            <SectionHeading text={t('weeklyReport.firstStar.title')} testId="weekly-report-first-star-title" />
            <h3
                className="mt-12 text-5xl sm:text-7xl lg:text-8xl font-extralight tracking-tight leading-[1.05]"
                data-testid="weekly-report-first-star-name"
            >
                {star.name}
            </h3>
            {star.title && (
                <p className="mt-6 text-lg sm:text-xl font-light opacity-70">{star.title}</p>
            )}
            {star.story && (
                <p className="mt-10 text-lg sm:text-xl font-light leading-relaxed max-w-2xl opacity-90">
                    {star.story}
                </p>
            )}
            <div className="mt-12 flex flex-wrap items-center gap-x-10 gap-y-4 text-base sm:text-lg">
                {star.recoveries !== null && star.recoveries !== undefined && (
                    <span style={{ color: PALETTE.recoveries }}>{star.recoveries} Recoveries</span>
                )}
                {star.mentions !== null && star.mentions !== undefined && (
                    <span style={{ color: PALETTE.engagement }}>{star.mentions} Guest Mentions</span>
                )}
                {star.satisfaction !== null && star.satisfaction !== undefined && (
                    <span style={{ color: PALETTE.reputation }}>{star.satisfaction} Satisfaction</span>
                )}
            </div>
        </section>
    );
};

const CustomerCommentSection = ({ data, t }) => {
    const comment = data.snapshot_json?.customer_comment;
    if (!comment || !(comment.text || '').trim()) return null;
    return (
        <section data-testid="weekly-report-customer-comment">
            <SectionHeading text={t('weeklyReport.customerComment.title')} testId="weekly-report-customer-comment-title" />
            <blockquote
                className="mt-12 text-3xl sm:text-4xl lg:text-5xl font-light italic leading-snug tracking-tight max-w-3xl"
                style={{ fontFamily: '"Georgia", serif' }}
                data-testid="weekly-report-customer-comment-text"
            >
                &ldquo;{comment.text}&rdquo;
            </blockquote>
            <p className="mt-8 text-xs uppercase tracking-[0.32em] opacity-50">
                {t('weeklyReport.customerComment.signoff')}
            </p>
        </section>
    );
};

const TeamCelebrationsSection = ({ data, t }) => {
    const items = data.snapshot_json?.team_celebrations || [];
    if (items.length === 0) return null;
    return (
        <section data-testid="weekly-report-team-celebrations">
            <SectionHeading text={t('weeklyReport.celebrations.title')} testId="weekly-report-celebrations-title" />
            <ul className="mt-12 space-y-8 sm:space-y-10">
                {items.map((c, idx) => (
                    <li
                        key={idx}
                        data-testid={`weekly-report-celebration-${idx}`}
                        className="text-2xl sm:text-3xl font-light leading-snug"
                    >
                        {c.text}
                    </li>
                ))}
            </ul>
        </section>
    );
};

const LookingBackSection = ({ data, t }) => {
    const lb = data.snapshot_json?.looking_back;
    if (!lb || (!lb.location_of_week_name && !lb.first_star_name)) return null;
    return (
        <section data-testid="weekly-report-looking-back">
            <SectionHeading text={t('weeklyReport.lookingBack.title')} testId="weekly-report-looking-back-title" />
            <div className="mt-12 space-y-10 sm:space-y-14">
                {lb.location_of_week_name && (
                    <div>
                        <p className="text-[11px] uppercase tracking-[0.28em] opacity-50 mb-3">
                            {t('weeklyReport.hallOfFame.locationOfWeek')}
                        </p>
                        <p className="text-2xl sm:text-3xl font-light tracking-tight">
                            {lb.location_of_week_name}
                        </p>
                    </div>
                )}
                {lb.first_star_name && (
                    <div>
                        <p className="text-[11px] uppercase tracking-[0.28em] opacity-50 mb-3">
                            {t('weeklyReport.firstStar.title')}
                        </p>
                        <p className="text-2xl sm:text-3xl font-light tracking-tight">
                            {lb.first_star_name}
                        </p>
                    </div>
                )}
            </div>
        </section>
    );
};

const MomentumSection = ({ data, t }) => {
    const text = data.snapshot_json?.momentum;
    if (!text || !text.trim()) return null;
    return (
        <section data-testid="weekly-report-momentum">
            <SectionHeading text={t('weeklyReport.momentum.title')} testId="weekly-report-momentum-title" />
            <p
                className="mt-12 text-xl sm:text-2xl font-light leading-relaxed max-w-2xl opacity-90"
                style={{ fontFamily: '"Georgia", serif' }}
            >
                {text}
            </p>
        </section>
    );
};

// ─────────────────────────────────────────────────────────────────────
// Shared primitives
// ─────────────────────────────────────────────────────────────────────
const SectionHeading = ({ text, testId }) => (
    <h2
        className="text-xs sm:text-sm uppercase tracking-[0.32em] opacity-60"
        data-testid={testId}
    >
        {text}
    </h2>
);

// ─────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────
const formatMetrics = (m) => {
    const engagements = (m.total_engagements ?? 0).toLocaleString();

    const valueNumber = m.total_value_generated ?? 0;
    const currency = m.currency || 'CAD';
    let value;
    try {
        value = new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency,
            maximumFractionDigits: 0,
        }).format(valueNumber);
    } catch {
        value = `$${Math.round(valueNumber).toLocaleString()}`;
    }

    const rating = (m.avg_rating === null || m.avg_rating === undefined)
        ? '—'
        : `${m.avg_rating}★`;

    const reviews = (m.reviews_count ?? 0).toLocaleString();
    const recoveries = (m.recoveries_count ?? 0).toLocaleString();

    // P_PHASE_4_8_REPUTATION_BASELINE_FALLBACK — render the Reputation
    // tile from the case-aware `reputation` block when present, else
    // fall back to the legacy parity rendering so older snapshots still
    // display correctly.
    const rep = m.reputation || null;
    let reputation;
    if (rep && rep.case === 'B') {
        const ratingVal = (rep.avg_rating === null || rep.avg_rating === undefined)
            ? '—' : `${rep.avg_rating}★`;
        const reviewsVal = (rep.reviews_count ?? 0).toLocaleString();
        const growth = rep.growth_since_baseline;
        const growthLabel = growth === null || growth === undefined
            ? null
            : (growth >= 0 ? `+${growth.toLocaleString()} since baseline`
                           : `${growth.toLocaleString()} since baseline`);
        reputation = {
            case: 'B',
            value: ratingVal,
            sub: growthLabel
                ? `${reviewsVal} reviews · ${growthLabel}`
                : `${reviewsVal} reviews`,
        };
    } else if (rep && rep.case === 'C') {
        reputation = { case: 'C', value: 'Not configured', sub: null };
    } else {
        // Case A (parity events exist) or backward compatibility with
        // snapshots created before this fallback layer existed.
        reputation = {
            case: 'A',
            value: rating,
            sub: `${reviews} reviews generated`,
        };
    }

    return { engagements, value, rating, reviews, recoveries, reputation };
};

const formatDelta = (pct) => {
    if (pct === null || pct === undefined) return null;
    if (pct === 0) {
        return { symbol: '·', value: '0%', color: 'rgba(245,241,232,0.45)' };
    }
    if (pct > 0) {
        return { symbol: '↑', value: `+${pct}%`, color: PALETTE.deltaUp };
    }
    return { symbol: '↓', value: `${pct}%`, color: PALETTE.deltaDown };
};
